import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { ThemeProvider } from "@mui/material/styles";
import { createBrkTheme } from "@/theme/theme";
import { schemes } from "@/theme/tokens";
import {
  resolveThemeMode,
  type ThemeChoice,
  type ThemeMode,
} from "@/theme/themeChoice";
import { readThemeChoice, writeThemeChoice } from "@/lib/devicePreferences";

// Emotion's styles are PREPENDED to <head> so Tailwind's utilities still win a
// specificity tie while pages mix the two. There is no CssBaseline: Tailwind's
// preflight is the baseline, as it already is for every existing page.
const cache = createCache({ key: "brk", prepend: true });

// The broker's theme (BRK-S245). They choose Day, Night or System from the user
// menu; System follows the OS live. The choice is a device preference
// (localStorage), and the resolved scheme drives both halves of the UI at
// once: `data-theme` on <html> for every Tailwind `brk-*` utility, and the MUI
// theme. index.html's script sets data-theme before first paint; this keeps it.
const DARK_QUERY = "(prefers-color-scheme: dark)";

function subscribeToOsScheme(onChange: () => void): () => void {
  if (typeof window.matchMedia !== "function") return () => {};
  const query = window.matchMedia(DARK_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

/** jsdom and very old browsers have no matchMedia: they get Day. */
function osPrefersDark(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia(DARK_QUERY).matches
  );
}

interface ThemeChoiceValue {
  choice: ThemeChoice;
  /** The scheme on screen: the choice, with System resolved. */
  mode: ThemeMode;
  setChoice: (choice: ThemeChoice) => void;
}

const ThemeChoiceContext = createContext<ThemeChoiceValue | null>(null);

export function BrkThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(() =>
    readThemeChoice(),
  );
  const prefersDark = useSyncExternalStore(
    subscribeToOsScheme,
    osPrefersDark,
    () => false,
  );
  const mode = resolveThemeMode(choice, prefersDark);

  // Before paint, so a switch recolours the Tailwind and MUI halves together.
  // A phone's address bar follows too: every shell's header is the theme's
  // panel (BRK-S249), and index.html's script set it for the first paint.
  useLayoutEffect(() => {
    document.documentElement.dataset.theme = mode;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", schemes[mode].panel);
  }, [mode]);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    writeThemeChoice(next);
  }, []);

  const theme = useMemo(() => createBrkTheme(mode), [mode]);
  const value = useMemo(
    () => ({ choice, mode, setChoice }),
    [choice, mode, setChoice],
  );

  return (
    <ThemeChoiceContext.Provider value={value}>
      <CacheProvider value={cache}>
        <ThemeProvider theme={theme}>{children}</ThemeProvider>
      </CacheProvider>
    </ThemeChoiceContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useThemeChoice(): ThemeChoiceValue {
  const value = useContext(ThemeChoiceContext);
  if (!value) {
    throw new Error("useThemeChoice must be used inside BrkThemeProvider");
  }
  return value;
}
